import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@sabay/ui-kit';
import { getDiscoverPosts, type PublicDiscoverPost } from '@/lib/api';
import { pickContent, pickTitle } from '@/lib/i18n-pick';
import { useLocale, useTk } from '@/lib/i18n';

interface State {
  status: 'loading' | 'success' | 'error';
  items: PublicDiscoverPost[];
}

/**
 * Koom GO 首页「探店推荐」2 列瀑布流。
 *
 * 视觉规格（DESIGN.md §Components > Discover Waterfall Card）：
 *   - 容器：CSS multi-column `columns-2 gap-3`
 *   - 单卡：`break-inside-avoid` 防止跨列裂开
 *   - 卡片：cover 图（保持原始 aspect）+ 三朵装饰花 SVG + 2 行标题 + 1 行商家
 *   - 三朵花是装饰元素（PRODUCT.md / DESIGN.md），不是评分；不要把它和 reviewScore 绑定
 *
 * 数据源：`getDiscoverPosts({ cityCode, pageSize: 10 })`，按发布时间倒序。
 *
 * 状态：
 *   - loading → 6 张 skeleton
 *   - error / 空数据 → 显示 SectionHeader + EmptyState（与原 FeaturedDiscoverSection 保持一致）
 *   - success → 瀑布流卡片
 */
export function DiscoverWaterfall({
  cityCode,
}: {
  cityCode: string;
}): React.ReactElement {
  const tk = useTk();
  const locale = useLocale();
  const [state, setState] = useState<State>({ status: 'loading', items: [] });

  useEffect(() => {
    const ac = new AbortController();
    setState({ status: 'loading', items: [] });
    getDiscoverPosts({ cityCode, pageSize: 10 }, ac.signal)
      .then((res) => setState({ status: 'success', items: res.items }))
      .catch(() => {
        if (!ac.signal.aborted) setState({ status: 'error', items: [] });
      });
    return () => ac.abort();
  }, [cityCode]);

  const isEmpty =
    state.status === 'error' ||
    (state.status === 'success' && state.items.length === 0);

  return (
    <section aria-label="discover waterfall" className="space-y-3">
      <SectionHeader
        title={tk('home.featured.discover.title')}
        href="/discover"
        action={tk('home.action.view_all')}
      />

      {state.status === 'loading' ? (
        <div className="columns-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="mb-3 break-inside-avoid space-y-2">
              <Skeleton
                className={[
                  'w-full rounded-lg',
                  i % 3 === 0 ? 'aspect-[4/5]' : 'aspect-[3/4]',
                ].join(' ')}
              />
              <Skeleton shape="text" className="h-4 w-3/4" />
              <Skeleton shape="text" className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : isEmpty ? (
        <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-0 px-4 py-8 text-center text-sm text-neutral-500">
          {tk('home.featured.discover.empty')}
        </div>
      ) : (
        <div className="columns-2 gap-3">
          {state.items.map((post) => {
            const title = pickTitle(post, locale);
            const content = pickContent(post.contentI18n, locale);
            return (
              <Link
                key={post.id}
                to={`/discover/${encodeURIComponent(post.id)}`}
                className="
                  mb-3 break-inside-avoid block group
                  rounded-lg overflow-hidden border border-neutral-100 bg-neutral-0
                  transition active:scale-[0.99]
                "
              >
                <CoverImage post={post} title={title} />
                <div className="p-2.5 space-y-1">
                  <FlowerRow />
                  <h3 className="text-[13px] font-medium text-neutral-900 leading-snug line-clamp-2 group-hover:text-brand-primary-700 transition-colors">
                    {title}
                  </h3>
                  {content ? (
                    <p className="text-[11px] text-neutral-600 leading-snug line-clamp-2">
                      {content}
                    </p>
                  ) : null}
                  <p className="text-[10px] text-neutral-400 truncate pt-0.5">
                    {post.merchantNameZh}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function CoverImage({
  post,
  title,
}: {
  post: PublicDiscoverPost;
  title: string;
}): React.ReactElement {
  // Pseudo-random aspect from id hash so masonry has varied tile heights
  // even though we don't know the real image aspect from the API.
  const aspect = aspectForId(post.id);

  if (post.coverImageUrl) {
    return (
      <div
        className={`w-full bg-neutral-100 bg-cover bg-center ${aspect}`}
        style={{ backgroundImage: `url(${post.coverImageUrl})` }}
        role="img"
        aria-label={title}
      />
    );
  }
  return (
    <div
      className={`w-full grid place-items-center bg-brand-primary-100 ${aspect}`}
      role="img"
      aria-label={title}
    >
      <span className="text-3xl font-bold text-neutral-900/40">
        {(title || '?').slice(0, 1)}
      </span>
    </div>
  );
}

function aspectForId(id: string): string {
  // Stable hash → choose one of three aspect ratios for visual variety.
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  const slot = Math.abs(hash) % 3;
  if (slot === 0) return 'aspect-[4/5]';
  if (slot === 1) return 'aspect-[3/4]';
  return 'aspect-[1/1]';
}

// ────────────────────────────────────────────────────────────────────────────
// 三朵装饰花（DESIGN.md / PRODUCT.md：装饰元素，不是评分）
// ────────────────────────────────────────────────────────────────────────────

function FlowerRow(): React.ReactElement {
  return (
    <div className="flex items-center gap-0.5 text-brand-primary-500" aria-hidden>
      <Flower />
      <Flower />
      <Flower />
    </div>
  );
}

function Flower(): React.ReactElement {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 12 12"
      fill="currentColor"
      aria-hidden
    >
      {/* 5 ellipse petals around a center disc — abstract daisy */}
      <ellipse cx="6" cy="2.2" rx="1.2" ry="2" />
      <ellipse cx="6" cy="9.8" rx="1.2" ry="2" />
      <ellipse cx="2.2" cy="6" rx="2" ry="1.2" />
      <ellipse cx="9.8" cy="6" rx="2" ry="1.2" />
      <circle cx="6" cy="6" r="1.4" fill="#131009" />
    </svg>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function SectionHeader({
  title,
  href,
  action,
}: {
  title: string;
  href: string;
  action: string;
}): React.ReactElement {
  return (
    <div className="flex items-end justify-between">
      <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
      <Link
        to={href}
        className="text-xs font-medium text-brand-primary-700 hover:text-brand-primary-800"
      >
        {action} ›
      </Link>
    </div>
  );
}
